import { APP_BLOB_BINDINGS_MAX_BYTES, appBlobBucketBindingSchema, blobBindingsFileSchema } from "./blob-bindings.js";
import { z } from "zod";
//#region src/claim-check-bindings.ts
/**
* Claim-check bindings: the CP-to-DP desired-state member and the pod file of the claim-check
* buckets (2026-10-13-blob-buckets, stage 6 contracts, R-102 to R-105).
*
* A claim-check payload is the body of a large event that the SDK writes to a bucket and replaces
* with a marker in the event (`CLAIM_CHECK_KEY` of `@zion/sdk-contracts`). Since R-102 each app
* with `platform.eventBus` has one reserved org-app bucket of the logical name
* `BLOB_BUCKET_CLAIM_CHECK_LOGICAL_NAME` (`claimcheck`) per (data plane, org, app). A library pair
* also writes into the bucket of the receiver: each side gets `objectCreator` on the other's
* `claimcheck` bucket (R-103).
*
* The chain (R-104):
*
* 1. release-engine (WS-58) writes one {@link AppClaimCheckBindings} per app into
*    `AppDesiredState.claimCheckBindings`, only for a data plane whose last status report names
*    `BLOB_CLAIMCHECK_CAPABILITY`. `own` holds the app's own `claimcheck` bucket per org. `peers`
*    holds, per receiver app slug, the receiver's `claimcheck` bucket per org, for each library pair
*    of R-103.
* 2. The operator program (WS-57) renders one {@link ClaimCheckBindingsFile} per app into its own
*    ConfigMap and mounts it read-only at {@link CLAIM_CHECK_BINDINGS_MOUNT_PATH}, with
*    {@link CLAIM_CHECK_BINDINGS_FILE_ENV}, on every pod of the app that it gives NATS credentials:
*    fixtures, pool pods and worker pods.
* 3. The SDK writers (node WS-32a, python WS-32c, rust WS-32d) look up the bucket of one write with
*    {@link resolveClaimCheckBinding}: `own` for a plain publish, `peers[<receiver>]` for a library
*    publish. With no binding, the SDK keeps the legacy store until the R-099 removal.
*
* `bindings.json` keeps its shape and its bound of 16 names. The name `claimcheck` never enters it
* (R-104 item 3): an older SDK refuses a file with a new key or a 17th name, and would lose every
* declared bucket.
*
* Two org id spaces, as in `blob-bindings.ts` (R-060 item 3). Each is a lower-case UUID, so one key
* grammar serves both: the `orgs` keys of the desired-state member are CP org ids, and the `orgs`
* keys of the pod file are DP-local org ids. The operator program maps each CP key with
* `ResidentOrgDirectory.dpOrgIdFor` and leaves out an org with no pairing (defaults row F3).
*
* The shared fixtures `cloud/platform-sdk/fixtures/claim-check-bindings/` hold valid and invalid
* files and the expected lookups; the node, Python and Rust parser tests read them.
*
* Strict at every depth. No `.transform()`. Pure: no clock, no filesystem, no network.
*/
/** The env var that names the claim-check bindings file inside an app pod (R-104 item 2). */
const CLAIM_CHECK_BINDINGS_FILE_ENV = "ZION_CLAIM_CHECK_BINDINGS_FILE";
/**
* The path the operator program mounts the claim-check bindings file at, read-only (R-104 item 2).
* It sits under `/etc/zion`, beside `BLOB_BINDINGS_MOUNT_PATH`, and never under `/var/run/zion`,
* where the signer sidecar holds a read-only mount (R-008). The Node, Python and Rust SDKs carry the
* same literal.
*/
const CLAIM_CHECK_BINDINGS_MOUNT_PATH = "/etc/zion/claim-check-bindings/claim-check.json";
/** The one version of {@link ClaimCheckBindingsFile}. A reader refuses every other value. */
const CLAIM_CHECK_BINDINGS_FILE_VERSION = 1;
/**
* The most receiver app slugs one `peers` map holds: 16 (R-104 item 1). A map with more entries is
* refused whole, in the desired state and in the pod file. The producer (WS-58) keeps at most 16
* receivers per app; it keeps the first 16 receiver slugs in byte order and leaves out the rest, so
* a sender to a 17th receiver keeps the legacy store for it.
*/
const CLAIM_CHECK_PEERS_MAX = 16;
/**
* The org map of `blob-bindings.ts`, the one declaration (R-104 item 1): lower-case UUID org id to
* one `BlobBucketPhysical`, at most `BLOB_BINDINGS_ORGS_MAX` entries. It is the `orgs` member of the
* `org` arm of `appBlobBucketBindingSchema`.
*/
const orgsSchema = appBlobBucketBindingSchema.options[0].shape.orgs;
/** The app slug grammar of the bindings file: 2 to 64 characters of `RESOURCE_SLUG_PATTERN`. */
const appSlugSchema = blobBindingsFileSchema.shape.app;
/**
* The `claimcheck` buckets of one app, per org: `{ orgs }`. Strict. `orgs` may be empty: the SDK
* then finds no binding for any org and keeps the legacy store.
*/
const claimCheckOrgBindingsSchema = z.object({ orgs: orgsSchema }).strict();
/** The `peers` map: receiver app slug to its {@link ClaimCheckOrgBindings}, at most 16 slugs. */
const peersSchema = z.record(appSlugSchema, claimCheckOrgBindingsSchema).refine((peers) => Object.keys(peers).length <= 16, { message: `at most 16 peers` });
/** Adds an issue at `['peers', <slug>]` when `peers` names the app itself. */
function refineNoSelfPeer(self, peers, ctx) {
	if (Object.hasOwn(peers, self)) ctx.addIssue({
		code: "custom",
		path: ["peers", self],
		message: "a peer is another app: the app reaches its own bucket through own"
	});
}
/**
* The claim-check bindings of one app in the desired state (R-104 item 1): `{ appSlug, own, peers }`.
*
* - `appSlug`: the app slug, in the grammar of the bindings file. `AppDesiredStateSchema` refuses a
*   value other than the entry's `appId`.
* - `own`: the app's own `claimcheck` bucket per CP org id, `ready` binding rows only.
* - `peers`: per receiver app slug, the receiver's `claimcheck` bucket per CP org id, for each
*   library pair of R-103 whose grant release-engine wrote. At most {@link CLAIM_CHECK_PEERS_MAX}
*   slugs. The app's own slug is never a key. The producer leaves out a receiver with no org, so
*   it never writes `{ orgs: {} }` under `peers`; a reader accepts it and finds no binding.
*
* The member as compact JSON holds at most `APP_BLOB_BINDINGS_MAX_BYTES` (990,000) bytes. The pod
* file root `{"version":1,"app":` is 8 bytes longer than the member root `{"appSlug":`, and the
* operator program maps each org id to another id of the same length or leaves the org out. So
* every file rendered from a member that parses holds at most 990,008 bytes, under the 1,000,000
* bytes of one ConfigMap. The byte check runs only on a member that passes every other check, so a
* member over a count cap fails with the count message alone. The producer (WS-58) counts the member
* in its byte budget and stops new rows before the bound.
*
* Refusals, each a `ZodError`: an unknown key at any depth; a missing `own` or `peers`; a slug or an
* org id outside its grammar; a physical bucket outside `blobBucketPhysicalSchema`; more than 16
* peers (path `['peers']`); a peer key equal to `appSlug` (path `['peers', <slug>]`); a member over
* the byte cap (the root path). A conforming member parses to itself. Pure.
*/
const appClaimCheckBindingsSchema = z.object({
	appSlug: appSlugSchema,
	own: claimCheckOrgBindingsSchema,
	peers: peersSchema
}).strict().superRefine((member, ctx) => refineNoSelfPeer(member.appSlug, member.peers, ctx)).refine((member) => JSON.stringify(member).length <= APP_BLOB_BINDINGS_MAX_BYTES, {
	message: `at most ${APP_BLOB_BINDINGS_MAX_BYTES} bytes of compact JSON`,
	when: (payload) => payload.issues.length === 0
});
/**
* The claim-check bindings file the operator program mounts into each app pod with NATS
* credentials (R-104 item 2): `{ version: 1, app, own, peers }`.
*
* - `version`: {@link CLAIM_CHECK_BINDINGS_FILE_VERSION}. A reader refuses every other value.
* - `app`: the app slug.
* - `own` and `peers`: the members of {@link AppClaimCheckBindings}, each `orgs` key a DP-local org
*   id. At most {@link CLAIM_CHECK_PEERS_MAX} peers, and `app` is never a peer key.
*
* A reader refuses any other version, an unknown key at any depth and a malformed entry, at the
* paths of {@link appClaimCheckBindingsSchema}; it never repairs a file. Strict. Pure.
*/
const claimCheckBindingsFileSchema = z.object({
	version: z.literal(1),
	app: appSlugSchema,
	own: claimCheckOrgBindingsSchema,
	peers: peersSchema
}).strict().superRefine((file, ctx) => refineNoSelfPeer(file.app, file.peers, ctx));
/**
* Resolve the bucket of one claim-check write. The one lookup rule of the SDK writers (WS-32a,
* WS-32c, WS-32d); `fixtures/claim-check-bindings/cases.json` holds the same rule as data.
*
* Input: `file`, the output of {@link claimCheckBindingsFileSchema}; `receiver`, the receiver app
* slug of a library publish (`publishToLibrary`, `publishToConsumer`), or `null` for a plain
* publish; `orgId`, the DP-local envelope org of the event, or `null` for an event with no org.
*
* Rules, in order:
* 1. `orgId` is `null`: `not-bound`.
* 2. `receiver` is `null`: `bound` with `own.orgs[orgId]` when `orgId` is an own key of
*    `own.orgs`, else `not-bound`.
* 3. `receiver` is not an own key of `peers`: `not-bound`. An inherited name such as `constructor`
*    is not an own key, and the app's own slug is never a key.
* 4. `orgId` is not an own key of `peers[receiver].orgs`: `not-bound`.
* 5. Otherwise `bound` with that org's bucket.
*
* Pure; never throws.
*/
function resolveClaimCheckBinding(file, receiver, orgId) {
	if (orgId === null) return { kind: "not-bound" };
	let bindings = file.own;
	if (receiver !== null) bindings = Object.hasOwn(file.peers, receiver) ? file.peers[receiver] : void 0;
	if (bindings === void 0) return { kind: "not-bound" };
	const physical = Object.hasOwn(bindings.orgs, orgId) ? bindings.orgs[orgId] : void 0;
	return physical === void 0 ? { kind: "not-bound" } : {
		kind: "bound",
		physical
	};
}
//#endregion
export { CLAIM_CHECK_BINDINGS_FILE_ENV, CLAIM_CHECK_BINDINGS_FILE_VERSION, CLAIM_CHECK_BINDINGS_MOUNT_PATH, CLAIM_CHECK_PEERS_MAX, appClaimCheckBindingsSchema, claimCheckBindingsFileSchema, claimCheckOrgBindingsSchema, resolveClaimCheckBinding };

//# sourceMappingURL=claim-check-bindings.js.map